import dropDownIcon from '@/assets/icons/elements/drop-down.svg';
import emailIcon from '@/assets/icons/elements/email.svg';
import longTextIcon from '@/assets/icons/elements/long-text.svg';
import numberIcon from '@/assets/icons/elements/number.svg';
import paragraphIcon from '@/assets/icons/elements/paragraph.svg';
import phoneIcon from '@/assets/icons/elements/phone.svg';
import sectionIcon from '@/assets/icons/elements/section.svg';
import shortTextIcon from '@/assets/icons/elements/short-text.svg';
import textIcon from '@/assets/icons/elements/text.svg';
import uploadIcon from '@/assets/icons/elements/upload.svg';

import type { FieldConfig } from './fieldConfig';

/**
 * The questionnaire element catalogue — every element type declared exactly once.
 *
 * WHY A REGISTRY AND NOT A SWITCH IN EACH COMPONENT. Four separate places need to know about an
 * element type: the palette (label + icon), the config panel (which editors to show), the
 * renderer (how to draw it), and — from Phase 7 — the applicant runner. The reference
 * implementation spreads that across four files with a long `if/else` chain in each, which is why
 * its own `state` / `district` / `city` entries were added to the palette and then forgotten
 * everywhere else. Declaring each type once here means adding element #18 is one entry, not four
 * edits, and it cannot be half-added.
 *
 * ICONS. The designer's artwork bakes its colour in (`stroke="#666666"`), so an `<img>` cannot
 * follow a hover or active state. They are rendered through `MaskIcon`, which paints the SVG's
 * shape with `currentColor` — the same approach `stepIcons.tsx` already uses for the workflow
 * step artwork, and the reason we do not copy the reference's `filter: hue-rotate(224deg)` hack
 * (an approximation of #0048FF that drifts from the token).
 *
 * Several types share an icon because the designer had not drawn theirs yet and reused a
 * placeholder — Date and Date Range both take the Section glyph, and Number / State / District /
 * City / Address all take the Number glyph. That is faithful to the reference, and swapping in
 * real artwork later is one line each.
 */

/** Which config editors an element type needs. Read by the config panel. */
export interface ElementCapabilities {
  /**
   * False for the types whose answer is chosen, not typed — a placeholder describes an empty
   * text box, and there is no empty text box to describe.
   */
  placeholder: boolean;
  /** Editable option list. */
  options: boolean;
  /** A chain of dependent dropdowns (Country to State to District). Drop Down only. */
  cascade: boolean;
  /** Per-line title + placeholder for Address. */
  addressSubfields: boolean;
  /** The "Validation Error Message" input. */
  validationMessage: boolean;
}

export interface ElementDefinition {
  /** Stable identifier, persisted as `questionnaire_fields.field_type`. */
  id: string;
  /** Palette entry and config-card heading. */
  label: string;
  /** SVG path, for MaskIcon. */
  icon: string;
  /** Hint text in the config panel's Title input. */
  titleHint: string;
  /**
   * The config panel's label for the placeholder input. Consent reads as a statement the
   * applicant agrees to, not as grey hint text, so calling it "Placeholder" would misdescribe
   * the only content the element has.
   */
  placeholderLabel: string;
  /** Applied when the element is added. */
  defaults: {
    label: string;
    placeholder: string;
    validationMessage: string;
    config: FieldConfig;
  };
  capabilities: ElementCapabilities;
}

/** Everything typed: a plain text box with a validation message. */
const TYPED: ElementCapabilities = {
  placeholder: true,
  options: false,
  cascade: false,
  addressSubfields: false,
  validationMessage: true,
};

/** Single/Multiple Choice: an option list, no placeholder. */
const CHOICE: ElementCapabilities = {
  placeholder: false,
  options: true,
  cascade: false,
  addressSubfields: false,
  validationMessage: true,
};

/**
 * Drop Down: options plus cascades, and — matching the reference — NO validation message. A
 * select always holds one of its own options, so there is no invalid state to write a message
 * about.
 */
const DROPDOWN: ElementCapabilities = {
  placeholder: false,
  options: true,
  cascade: true,
  addressSubfields: false,
  validationMessage: false,
};

/**
 * State / District / City. Structurally a dropdown whose options the tenant types in, but
 * WITHOUT cascades: chaining them is what Drop Down's own dependent levels are for, and offering
 * two ways to build the same State→City pair would leave admins guessing which one the renderer
 * honours.
 */
const PLACE: ElementCapabilities = {
  placeholder: false,
  options: true,
  cascade: false,
  addressSubfields: false,
  validationMessage: true,
};

const ADDRESS: ElementCapabilities = {
  placeholder: false,
  options: false,
  cascade: false,
  addressSubfields: true,
  validationMessage: true,
};

/** The reference's three address lines, and the fallback when a field stores none. */
export const DEFAULT_ADDRESS_SUBFIELDS = [
  { id: 'unit', title: 'Unit No.', placeholder: 'Enter unit / flat no.' },
  { id: 'building', title: 'Building Name', placeholder: 'Enter building / apartment name' },
  { id: 'street', title: 'Street/Area', placeholder: 'Enter street / area / locality' },
] as const;

/** Options given to a new choice/dropdown field so it renders as something immediately. */
const STARTER_OPTIONS = ['Option 1', 'Option 2', 'Option 3'];

function typed(
  id: string,
  label: string,
  icon: string,
  placeholder: string,
  overrides: Partial<Omit<ElementDefinition, 'id' | 'label' | 'icon'>> = {},
): ElementDefinition {
  return {
    id,
    label,
    icon,
    titleHint: `Ex. What is your current ${label.toLowerCase()}?`,
    placeholderLabel: 'Placeholder',
    defaults: { label: '', placeholder, validationMessage: '', config: {} },
    capabilities: TYPED,
    ...overrides,
  };
}

/**
 * Palette order, which is also the reference's sidebar order. The array is the ordering — do not
 * sort it at the call site.
 */
export const ELEMENTS: readonly ElementDefinition[] = [
  typed('long-text', 'Long Text', longTextIcon, 'Enter your answer here'),
  typed('short-text', 'Short Text', shortTextIcon, 'Enter your answer here'),
  typed('paragraph', 'Paragraph', paragraphIcon, 'Enter your paragraph details here...', {
    defaults: {
      label: 'Paragraph',
      placeholder: 'Enter your paragraph details here...',
      validationMessage: '',
      config: {},
    },
  }),
  {
    id: 'drop-down',
    label: 'Drop Down',
    icon: dropDownIcon,
    titleHint: 'Ex. Select State',
    placeholderLabel: 'Placeholder',
    defaults: {
      label: 'Select Option',
      placeholder: 'Select an option',
      validationMessage: '',
      config: { options: [...STARTER_OPTIONS] },
    },
    capabilities: DROPDOWN,
  },
  {
    id: 'single-choice',
    label: 'Single Choice',
    icon: sectionIcon,
    titleHint: 'Ex. Choose one option',
    placeholderLabel: 'Placeholder',
    defaults: {
      label: 'Choose Single Option',
      placeholder: 'Select an option',
      validationMessage: '',
      config: { options: [...STARTER_OPTIONS, 'Option 4'] },
    },
    capabilities: CHOICE,
  },
  {
    id: 'multiple-choice',
    label: 'Multiple Choice',
    icon: sectionIcon,
    titleHint: 'Ex. Choose all that apply',
    placeholderLabel: 'Placeholder',
    defaults: {
      label: 'Choose Multiple Option',
      placeholder: 'Select options',
      validationMessage: '',
      // Four DISTINCT options. The reference seeds
      // ["Option 1","Option 2","Option 1","Option 1","Option 1","Option 2"] — six entries with
      // four duplicates, which is mock noise rather than a design: duplicate labels in a
      // multi-select are indistinguishable to the applicant and ambiguous in the stored answer.
      config: { options: [...STARTER_OPTIONS, 'Option 4'] },
    },
    capabilities: CHOICE,
  },
  typed('date', 'Date', sectionIcon, 'DD/MM/YYYY', {
    titleHint: 'Ex. Date of birth',
    defaults: {
      label: 'Date of Birth',
      placeholder: 'DD/MM/YYYY',
      validationMessage: '',
      config: {},
    },
  }),
  typed('date-range', 'Date Range', sectionIcon, 'DD/MM/YYYY', {
    titleHint: 'Ex. Period of employment',
    defaults: {
      label: 'Date Picker',
      placeholder: 'DD/MM/YYYY',
      validationMessage: '',
      config: {},
    },
  }),
  typed('number', 'Number', numberIcon, '1234567890', {
    defaults: {
      // 1234567890, not the reference's "1234567789" — a placeholder is shown verbatim to the
      // applicant, and a transposed digit reads as a broken example.
      label: 'Number',
      placeholder: '1234567890',
      validationMessage: '',
      config: {},
    },
  }),
  {
    id: 'address',
    label: 'Address',
    icon: numberIcon,
    titleHint: 'Ex. Residential address',
    placeholderLabel: 'Placeholder',
    defaults: {
      label: 'Residential Address',
      placeholder: '',
      validationMessage: '',
      config: { addressSubfields: DEFAULT_ADDRESS_SUBFIELDS.map((s) => ({ ...s })) },
    },
    capabilities: ADDRESS,
  },
  typed('email', 'Email', emailIcon, 'example@domain.com', {
    titleHint: 'Ex. What is your email address?',
    defaults: {
      label: 'Email Address',
      placeholder: 'example@domain.com',
      validationMessage: '',
      config: {},
    },
  }),
  typed('mobile-number', 'Mobile Number', phoneIcon, '+91 98765 43210', {
    titleHint: 'Ex. What is your mobile number?',
    defaults: {
      // +91, not the reference's "+1 234 567 8900". Every applicant journey in this product is
      // Indian — the session view and sessions list both prefix +91 — so a US example would be
      // the only US number on the screen.
      label: 'Mobile Number',
      placeholder: '+91 98765 43210',
      validationMessage: '',
      config: {},
    },
  }),
  typed('upload', 'Upload', uploadIcon, 'Upload document', {
    titleHint: 'Ex. Upload your address proof',
    defaults: {
      label: 'Upload Document',
      placeholder: 'Upload document',
      validationMessage: '',
      config: {},
    },
  }),
  {
    id: 'consent-text',
    label: 'Consent Text',
    icon: textIcon,
    titleHint: 'Ex. Consent and Declaration',
    placeholderLabel: 'Consent Statement',
    defaults: {
      label: 'Consent and Declaration',
      placeholder: 'I agree to the terms and conditions',
      validationMessage: '',
      config: {},
    },
    capabilities: TYPED,
  },
];

/**
 * Types that are no longer offered in the palette but are still fully understood.
 *
 * WHY THEY ARE HERE AND NOT DELETED. `field_type` is persisted, so any questionnaire already saved
 * with one of these types still has to open, render and save exactly as before. Deleting the
 * definitions would drop those fields through `elementFor`'s unknown-type fallback and turn a
 * working dropdown into a TEXT BOX — the option list still in `config` but nothing rendering it,
 * and re-saving would keep the field silently broken. Retiring is not the same as removing.
 *
 * WHY THEY LEFT THE PALETTE. `state` / `district` / `city` read as a State→District→City chain but
 * were three UNRELATED dropdowns: `capabilities: PLACE` sets `cascade: false`, so nothing linked
 * them. Choosing "Gujarat" left District offering its seeded `Option 1 / Option 2 / Option 3` —
 * placeholder text shipped to a real applicant. Drop Down's dependent levels do this properly, and
 * offering two mechanisms for the same State→City pair is exactly what the old comment here warned
 * would leave admins guessing which one the renderer honours.
 *
 * Nothing else needs to change for these to keep working: the renderer's `case 'state'` /
 * `'district'` / `'city'` branch and `answers.ts`'s validation message stay as they are.
 */
const RETIRED_ELEMENTS: readonly ElementDefinition[] = [
  {
    id: 'state',
    label: 'State',
    icon: numberIcon,
    titleHint: 'Ex. Select your state',
    placeholderLabel: 'Placeholder',
    defaults: {
      label: 'State',
      placeholder: 'Select a state',
      validationMessage: '',
      config: { options: ['Gujarat', 'Maharashtra'] },
    },
    capabilities: PLACE,
  },
  {
    id: 'district',
    label: 'District',
    icon: numberIcon,
    titleHint: 'Ex. Select your district',
    placeholderLabel: 'Placeholder',
    defaults: {
      label: 'District',
      placeholder: 'Select a district',
      validationMessage: '',
      config: { options: [...STARTER_OPTIONS] },
    },
    capabilities: PLACE,
  },
  {
    id: 'city',
    label: 'City',
    icon: numberIcon,
    titleHint: 'Ex. Select your city',
    placeholderLabel: 'Placeholder',
    defaults: {
      label: 'City',
      placeholder: 'Select a city',
      validationMessage: '',
      config: { options: ['Ahmedabad', 'Vadodara'] },
    },
    capabilities: PLACE,
  },
];

// Palette types AND retired ones. `ELEMENTS` alone is the palette; this map is "everything this
// build knows how to render", which is a strictly larger set.
const BY_ID = new Map([...ELEMENTS, ...RETIRED_ELEMENTS].map((e) => [e.id, e]));

/**
 * The definition for a stored `field_type`, or a safe stand-in when the type is unrecognised.
 *
 * WHY A FALLBACK RATHER THAN THROWING. `field_type` is an open string, so the set of values in
 * the database is not the set this build knows about — a questionnaire saved by a newer build, or
 * by an older palette, will contain types with no entry here. Failing on one would take down the
 * whole builder, leaving an admin unable to even delete the field that broke it. Rendering it as
 * a text box keeps the form editable and the field's own type string intact, so opening and
 * saving in this build never silently rewrites it.
 *
 * This is what keeps the pre-Phase-2 `heading` and `section` types working after the palette
 * changes underneath them.
 */
export function elementFor(fieldType: string): ElementDefinition {
  const known = BY_ID.get(fieldType);
  if (known) {
    return known;
  }

  return {
    id: fieldType,
    // The raw type is shown deliberately: "heading" tells an admin exactly which field this is,
    // where a generic "Field" would hide it.
    label: humaniseFieldType(fieldType),
    icon: shortTextIcon,
    titleHint: 'Enter a question',
    placeholderLabel: 'Placeholder',
    defaults: { label: '', placeholder: '', validationMessage: '', config: {} },
    capabilities: TYPED,
  };
}

/** True when the palette actually offers this type — the config panel flags the rest. */
export function isKnownElement(fieldType: string): boolean {
  return BY_ID.has(fieldType);
}

/** `long-text` → "Long text", `dateRange` → "Date range". */
function humaniseFieldType(fieldType: string): string {
  const spaced = fieldType.replace(/[-_]+/g, ' ').replace(/([a-z0-9])([A-Z])/g, '$1 $2');
  const trimmed = spaced.trim();
  return trimmed.length === 0
    ? 'Field'
    : trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
}
